import { motion } from "framer-motion";
import {
  ArrowRight, BarChart3, Bot, CheckCircle2, Cloud, FileText, Gauge,
  MessageCircleMore, ReceiptText, ShieldCheck, Sparkles, Users, Workflow,
} from "lucide-react";
import { Link } from "react-router-dom";
import Hero from "../components/Hero/Hero";
import Footer from "../components/Footer";
import analysisDashboard from "../assets/images/dashboard-image.png";

const capabilities = [
  { icon: FileText, title: "Records & Transactions", text: "Capture daily entries, supplier/customer records, payments and operational activity without scattered paperwork." },
  { icon: Gauge, title: "Powerful Dashboards", text: "See business performance, cash flow, activity and trends at a glance with role-specific dashboards." },
  { icon: Workflow, title: "Automation Engine", text: "Reduce repetitive work by automatically creating and updating records from the workflows you already follow." },
  { icon: MessageCircleMore, title: "WhatsApp Automation", text: "Keep customers and teams informed with transaction updates and notifications sent through WhatsApp workflows." },
  { icon: BarChart3, title: "Analytics & Reports", text: "Turn operational data into useful insights with reports, analysis and business summaries." },
  { icon: ShieldCheck, title: "Roles & Control", text: "Control access by user and role while keeping important business activity organized and traceable." },
];

const workflow = [
  ["01", "Enter once", "Record the transaction at the source instead of duplicating the same information across notebooks and systems."],
  ["02", "Automate the routine", "MarketMind creates related records and carries information forward through configured workflows."],
  ["03", "Communicate automatically", "WhatsApp and notifications can be triggered as part of the operational flow."],
  ["04", "See the result", "Dashboards and reports turn those records into a clear picture of the business."],
];

const metrics = [
  ["Single source of truth", "All core records in one connected workspace"],
  ["Cloud-first access", "Work from the office, laptop or supported devices"],
  ["Operational visibility", "Know what is happening without chasing files"],
  ["Built for real workflows", "Designed around agricultural and trading operations"],
];

export default function Home() {
  return (
    <div className="bg-[#f7fbfd] text-slate-900">
      <Hero />

      <section id="product" className="section bg-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid gap-10 lg:grid-cols-[0.85fr_1.15fr] lg:items-end">
            <div>
              <div className="section-kicker">MarketMind ERP</div>
              <h2 className="section-title">One workspace for the work that keeps your business moving.</h2>
            </div>
            <p className="max-w-2xl text-base leading-7 text-slate-600 lg:ml-auto">
              MarketMind is not just a place to store records. It connects operational entries, financial activity,
              reporting and communication so your team spends less time repeating work and more time running the business.
            </p>
          </div>

          <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {capabilities.map(({ icon: Icon, title, text }, index) => (
              <motion.div
                key={title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.25 }}
                transition={{ delay: index * 0.04, duration: 0.45 }}
                className="feature-card"
              >
                <div className="feature-icon"><Icon className="h-5 w-5" /></div>
                <h3 className="mt-5 text-lg font-bold text-[#003448]">{title}</h3>
                <p className="mt-2 text-sm leading-6 text-slate-600">{text}</p>
                <div className="mt-5 flex items-center gap-1 text-sm font-semibold text-[#00aef0]">Explore capability <ArrowRight className="h-4 w-4" /></div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section id="dashboards" className="section bg-[#003448] text-white">
        <div className="mx-auto grid max-w-7xl gap-14 px-4 sm:px-6 lg:grid-cols-[0.9fr_1.1fr] lg:items-center lg:px-8">
          <div>
            <div className="section-kicker section-kicker-light">Dashboards</div>
            <h2 className="section-title text-white">Your business at a glance — without digging through records.</h2>
            <p className="mt-5 max-w-xl text-base leading-7 text-slate-300">
              Replace scattered spreadsheets and end-of-day guesswork with dashboards that surface the numbers and activity
              your team needs to act.
            </p>
            <div className="mt-8 space-y-4">
              {[
                "Role-focused views for different users",
                "Operational and financial visibility",
                "Analysis dashboards with charts and trends",
                "Faster decisions from connected business data",
              ].map((item) => (
                <div key={item} className="flex gap-3 text-sm text-slate-200">
                  <CheckCircle2 className="mt-0.5 h-5 w-5 flex-none text-[#7ddcff]" />
                  <span>{item}</span>
                </div>
              ))}
            </div>
            <Link to="/pricing" className="mt-8 inline-flex btn-light px-5 py-3">See Plans <ArrowRight className="h-4 w-4" /></Link>
          </div>

          <div className="rounded-[28px] border border-white/10 bg-white/5 p-3 shadow-2xl">
            <img src={analysisDashboard} alt="MarketMind analysis dashboard" className="w-full rounded-2xl" />
          </div>
        </div>
      </section>

      <section id="automation" className="section bg-[#eaf8fe]">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="section-kicker">Automation</div>
            <h2 className="section-title">The best workflow is the one you do once.</h2>
            <p className="mt-5 text-base leading-7 text-slate-600">
              The new MarketMind automation layer is designed around a simple principle: enter information once,
              then let connected workflows take care of the repetitive records and follow-up steps.
            </p>
          </div>

          <div className="mt-12 grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            {workflow.map(([number, title, text]) => (
              <div key={number} className="rounded-3xl border border-[#c7eaf6] bg-white p-6">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-bold text-[#00aef0]">{number}</span>
                  <Bot className="h-5 w-5 text-[#003448]" />
                </div>
                <h3 className="mt-10 text-lg font-bold text-[#003448]">{title}</h3>
                <p className="mt-2 text-sm leading-6 text-slate-600">{text}</p>
              </div>
            ))}
          </div>

          <div className="mt-10 rounded-[28px] border border-[#c7eaf6] bg-white p-6 sm:p-8">
            <div className="grid items-center gap-8 lg:grid-cols-[1fr_1.2fr]">
              <div>
                <div className="feature-icon"><Workflow className="h-5 w-5" /></div>
                <h3 className="mt-5 text-2xl font-bold text-[#003448]">Automation that feels native to the product.</h3>
                <p className="mt-3 leading-7 text-slate-600">No extra tool, no second data entry screen. The workflow stays close to the records your team already uses.</p>
              </div>
              <div className="grid gap-3 sm:grid-cols-3">
                {["Input", "Process", "Output"].map((step, i) => (
                  <div key={step} className="rounded-2xl bg-[#f5fbfd] p-5 text-center">
                    <div className="mx-auto flex h-10 w-10 items-center justify-center rounded-full bg-[#003448] text-sm font-bold text-white">{i + 1}</div>
                    <p className="mt-3 font-semibold text-[#003448]">{step}</p>
                    <p className="mt-1 text-xs text-slate-500">{i === 0 ? "Record once" : i === 1 ? "Workflow runs" : "Records update"}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="whatsapp" className="section bg-white">
        <div className="mx-auto grid max-w-7xl gap-12 px-4 sm:px-6 lg:grid-cols-[1.05fr_0.95fr] lg:items-center lg:px-8">
          <div className="order-2 lg:order-1 rounded-[30px] border border-slate-200 bg-[#f7fbfd] p-4">
            <div className="rounded-[24px] bg-[#003448] p-5 text-white sm:p-7">
              <div className="flex items-center gap-3">
                <div className="rounded-2xl bg-white/10 p-3"><MessageCircleMore className="h-6 w-6 text-[#7ddcff]" /></div>
                <div>
                  <p className="text-sm font-semibold">WhatsApp Automation</p>
                  <p className="text-xs text-slate-300">Connected to business workflows</p>
                </div>
              </div>
              <div className="mt-8 space-y-3">
                {[
                  ["Transaction created", "Customer notification prepared"],
                  ["Payment recorded", "Receipt update sent"],
                  ["Workflow completed", "Team informed automatically"],
                ].map(([a,b]) => (
                  <div key={a} className="rounded-2xl bg-white/10 p-4">
                    <p className="text-xs uppercase tracking-[0.12em] text-slate-400">{a}</p>
                    <div className="mt-1 flex items-center gap-2 text-sm font-medium">
                      <CheckCircle2 className="h-4 w-4 text-[#7ddcff]" /> {b}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="order-1 lg:order-2">
            <div className="section-kicker">WhatsApp Automation</div>
            <h2 className="section-title">Keep people updated without making your team the messenger.</h2>
            <p className="mt-5 text-base leading-7 text-slate-600">
              Connect operational events to WhatsApp notifications so the right people receive useful updates at the right time.
            </p>
            <div className="mt-7 space-y-4">
              {["Transaction notifications", "Payment and receipt updates", "Workflow-triggered messages", "Better customer communication"].map(item => (
                <div key={item} className="flex items-center gap-3">
                  <div className="flex h-8 w-8 items-center justify-center rounded-full bg-[#eaf8fe] text-[#00aef0]"><CheckCircle2 className="h-4 w-4" /></div>
                  <span className="font-medium text-slate-700">{item}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section id="analytics" className="section bg-[#f7fbfd]">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid gap-8 lg:grid-cols-[1fr_1.05fr]">
            <div>
              <div className="section-kicker">Analytics & Reports</div>
              <h2 className="section-title">From raw records to clear business decisions.</h2>
              <p className="mt-5 max-w-xl leading-7 text-slate-600">
                Give owners and managers a faster way to understand activity, trends, payments and performance.
                The website should make this value obvious before a visitor ever opens the software.
              </p>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              {[
                [BarChart3, "Trend visibility", "See how your business is moving over time."],
                [ReceiptText, "Financial tracking", "Connect transactions and payment activity."],
                [Cloud, "Cloud access", "Reach your workspace wherever your team operates."],
                [Users, "Team visibility", "Give each user the right level of access."],
              ].map(([Icon, title, text]) => (
                <div key={title} className="feature-card">
                  <div className="feature-icon"><Icon className="h-5 w-5" /></div>
                  <h3 className="mt-4 font-bold text-[#003448]">{title}</h3>
                  <p className="mt-2 text-sm leading-6 text-slate-600">{text}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {metrics.map(([title, text]) => (
              <div key={title} className="rounded-2xl border border-slate-200 bg-white p-5">
                <p className="font-bold text-[#003448]">{title}</p>
                <p className="mt-2 text-sm leading-6 text-slate-500">{text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section bg-[#003448]">
        <div className="mx-auto max-w-5xl px-4 text-center sm:px-6 lg:px-8">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl bg-white/10 text-[#7ddcff]"><Sparkles /></div>
          <h2 className="mt-6 text-3xl font-bold tracking-tight text-white sm:text-5xl">Ready to replace manual work with a smarter system?</h2>
          <p className="mx-auto mt-5 max-w-2xl text-base leading-7 text-slate-300">
            See how MarketMind can fit into your current operation, team and workflow.
          </p>
          <div id="contact" className="mt-8 flex flex-col justify-center gap-3 sm:flex-row">
            <a href="mailto:sales@lightningtechnologies.net" className="btn-light px-6 py-3.5">Talk to Sales <ArrowRight className="h-4 w-4" /></a>
            <Link to="/pricing" className="btn-dark-outline px-6 py-3.5">View Pricing</Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
